import { FieldTable } from '@/_shared/components/field-table/index.js';
import { PackedGrid } from '@/_shared/components/packed-grid/index.js';
import { Section } from '@/_shared/components/section/index.js';
import { useTsRest } from '@/_shared/context/ts-rest.js';
import { Body1, tokens } from '@fluentui/react-components';
import { MoneyRegular } from '@fluentui/react-icons';
import { fromIso, toHumanReadable } from '@this/shared/dist/tools/datetime.js';
import { formatEur } from '@this/shared/dist/tools/number-format.js';

type PaymentsProps = {
    claimId: string;
    onDocumentsSelect: (documentIds: string[]) => void;
};

export function Payments({ claimId, onDocumentsSelect }: PaymentsProps) {
    const tsr = useTsRest();

    const { data } = tsr.claimDetails.getPaymentDocuments.useQuery({
        queryKey: ['claimDetails', 'getPaymentDocuments', claimId],
        queryData: {
            params: {
                claimId,
            },
        },
        refetchInterval: 30_000,
    });

    return (
        <Section
            header={{
                label: 'Zahlungsdokumente',
                color: 'default',
                icon: <MoneyRegular />,
            }}
            footer={
                <Body1 style={{ color: tokens.colorNeutralForeground4, fontStyle: 'italic' }}>
                    Zahlungsbelege und Belegsummen erscheinen, sobald diese von der KI oder einem Sachbearbeiter
                    bearbeitet wurden.
                </Body1>
            }
        >
            <PackedGrid>
                <FieldTable
                    fieldValue={data?.body.documents}
                    emptyMessage="Keine Zahlungsdokumente"
                    maxRows={10}
                    columnSizes={[
                        { key: 'processedAt', size: '1fr' },
                        { key: 'documentType', size: '1fr' },
                        { key: 'decision', size: '1fr' },
                        { key: 'documentAmount', size: '1fr', alignRight: true },
                        { key: 'payoutAmount', size: '1fr', alignRight: true },
                    ]}
                    tableCells={[
                        {
                            key: 'processedAt',
                            label: 'Verarbeitet am',
                            serializer: (value) => (
                                <>
                                    {value.processedAt
                                        ? toHumanReadable(fromIso(value.processedAt), 'date_short_2digit')
                                        : ''}
                                </>
                            ),
                        },
                        {
                            key: 'documentType',
                            label: 'Belegtyp',
                            onValueClick: (_event, _value, record) => {
                                onDocumentsSelect([`virtual-payment-decision-document:${record.id}`]);
                            },
                        },
                        {
                            key: 'decision',
                            label: 'Entscheidung',
                        },
                        {
                            key: 'documentAmount',
                            label: 'Betrag',
                            serializer: (value) => <>{formatEur(value.documentAmount)}</>,
                        },
                        {
                            key: 'payoutAmount',
                            label: 'Auszahlung',
                            serializer: (value) => (
                                <>{value.payoutAmount !== null ? formatEur(value.payoutAmount) : '—'}</>
                            ),
                        },
                    ]}
                    totalField={{
                        key: 'payoutAmount',
                        label: 'Summe',
                        serializer: (value) => <>{formatEur(value)}</>,
                    }}
                />
            </PackedGrid>
        </Section>
    );
}
